import React, { useState, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
    TextField,
    LinearProgress,
    Stack,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Paper
} from '@mui/material';
import {
    CloudUpload,
    FolderZip,
    Folder,
    InsertDriveFile,
    Close,
    CheckCircle,
    CloudDownload
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import axiosDirectApi from 'utils/axiosDirectApi';

const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks

const DatasetUploadDialog = ({ open, onClose, onSuccess, projectName, tenantId }) => {
    const [uploadMethod, setUploadMethod] = useState('zip');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [datasetName, setDatasetName] = useState('');
    const [datasetType, setDatasetType] = useState('detection');
    const [uploadUrl, setUploadUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatus, setUploadStatus] = useState('');
    const [error, setError] = useState('');

    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);
    const pollIntervalRef = useRef(null);

    // Dropzone for drag & drop
    const onDrop = useCallback((acceptedFiles) => {
        setSelectedFiles(acceptedFiles);
        setError('');
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: uploadMethod === 'zip' ? { 'application/zip': ['.zip'] } : undefined,
        multiple: uploadMethod !== 'zip'
    });

    const handleMethodChange = (event, newMethod) => {
        if (newMethod !== null) {
            setUploadMethod(newMethod);
            setSelectedFiles([]);
            setError('');
        }
    };

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles(files);
        setError('');
    };

    const handleRemoveFile = (index) => {
        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    // Upload large file in chunks
    const uploadFileInChunks = async (file, datasetId) => {
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

        // Create upload session
        const sessionResponse = await axiosDirectApi.post(`/cv/datasets/${datasetId}/upload/session`, {
            filename: file.name,
            total_chunks: totalChunks,
            total_size: file.size,
            upload_method: uploadMethod
        });

        const sessionId = sessionResponse.data.session_id;

        // Upload chunks
        for (let chunkNumber = 0; chunkNumber < totalChunks; chunkNumber++) {
            const start = chunkNumber * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const chunk = file.slice(start, end);

            const formData = new FormData();
            formData.append('chunk', chunk);

            await axiosDirectApi.post(
                `/cv/datasets/${datasetId}/upload/chunk?session_id=${sessionId}&chunk_number=${chunkNumber}`,
                formData,
                {
                    headers: { 'Content-Type': 'multipart/form-data' }
                }
            );

            setUploadProgress(((chunkNumber + 1) / totalChunks) * 90); // Reserve 10% for finalization
        }

        // Finalize upload
        await axiosDirectApi.post(`/cv/datasets/${datasetId}/upload/finalize?session_id=${sessionId}`);

        return sessionId;
    };

    const handleUpload = async () => {
        if (!datasetName) {
            setError('Vui lòng nhập tên dataset');
            return;
        }

        if (uploadMethod !== 'url' && selectedFiles.length === 0) {
            setError('Vui lòng chọn file để tải lên');
            return;
        }

        if (uploadMethod === 'url' && !uploadUrl) {
            setError('Vui lòng nhập URL');
            return;
        }

        setUploading(true);
        setError('');
        setUploadProgress(0);

        try {
            // Step 1: Create dataset
            setUploadStatus('Đang tạo dataset...');
            const createResponse = await axiosDirectApi.post('/cv/datasets', {
                name: datasetName,
                project_id: projectName,
                dataset_type: datasetType,
                format: 'yolo'
            });

            const datasetId = createResponse.data.id;
            const datasetTenantId = createResponse.data.tenant_id; // Get tenant_id from response
            setUploadProgress(10);

            // Step 2: Upload based on method
            if (uploadMethod === 'zip') {
                setUploadStatus('Đang tải lên file ZIP...');
                const file = selectedFiles[0];

                if (file.size > 100 * 1024 * 1024) {
                    // Large file - use chunked upload
                    await uploadFileInChunks(file, datasetId);
                } else {
                    // Small file - direct upload
                    const formData = new FormData();
                    formData.append('file', file);

                    await axiosDirectApi.post(`/cv/datasets/${datasetId}/upload`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                        onUploadProgress: (progressEvent) => {
                            const percentCompleted = Math.round((progressEvent.loaded * 80) / progressEvent.total + 10);
                            setUploadProgress(percentCompleted);
                        }
                    });
                }

                setUploadProgress(90);

                // Step 3: Extract and process (async with polling)
                try {
                    setUploadStatus('Đang giải nén và xử lý dữ liệu...');
                    const objectKey = `cv/${datasetTenantId}/${datasetId}/raw/${file.name}`;

                    console.log('[Upload] Starting extraction and processing (async)...');

                    // Call extract API - returns task_id immediately (non-blocking)
                    const extractResponse = await axiosDirectApi.post(`/cv/datasets/${datasetId}/extract-zip`, null, {
                        params: { zip_object_key: objectKey }
                    });

                    const taskId = extractResponse.data.task_id;
                    console.log('[Upload] Task started:', taskId);

                    // Poll task status until completion
                    pollIntervalRef.current = setInterval(async () => {
                        try {
                            const statusResponse = await axiosDirectApi.get(`/cv/tasks/${taskId}`);
                            const taskStatus = statusResponse.data;

                            console.log('[Upload] Task status:', taskStatus);

                            // Update progress based on task state
                            if (taskStatus.status === 'PROGRESS') {
                                const taskProgress = taskStatus.progress || 0;
                                // Map 0-100% of task to 90-100% of UI progress
                                setUploadProgress(90 + taskProgress / 10);
                                setUploadStatus(taskStatus.message || 'Đang xử lý...');
                            } else if (taskStatus.status === 'SUCCESS') {
                                if (pollIntervalRef.current) {
                                    clearInterval(pollIntervalRef.current);
                                    pollIntervalRef.current = null;
                                }
                                console.log('[Upload] Extraction completed successfully!');

                                // Success!
                                setUploadStatus('Hoàn thành! Dataset đã sẵn sàng');
                                setUploadProgress(100);
                                setUploading(false);

                                // Close dialog
                                setTimeout(() => {
                                    if (typeof onSuccess === 'function') {
                                        onSuccess(datasetId);
                                    }
                                    handleClose();
                                }, 1000);
                            } else if (taskStatus.status === 'FAILURE') {
                                if (pollIntervalRef.current) {
                                    clearInterval(pollIntervalRef.current);
                                    pollIntervalRef.current = null;
                                }
                                console.error('[Upload] Extraction failed:', taskStatus.error);
                                setError(taskStatus.error || 'Lỗi khi xử lý dataset');
                                setUploading(false);
                            } else {
                                // PENDING or other status
                                setUploadStatus('Đang chờ xử lý...');
                            }
                        } catch (pollErr) {
                            console.error('[Upload] Failed to poll task status:', pollErr);
                        }
                    }, 2000); // Poll every 2 seconds
                } catch (err) {
                    console.error('[Upload] Extraction failed:', err);
                    setError(err.response?.data?.detail || 'Lỗi khi xử lý dataset');
                    setUploading(false);
                }
            } else if (uploadMethod === 'folder' || uploadMethod === 'files') {
                setUploadStatus('Đang tải lên files...');
                const formData = new FormData();

                selectedFiles.forEach((file) => {
                    formData.append('files', file);
                });

                await axiosDirectApi.post(`/cv/datasets/${datasetId}/upload/batch`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round((progressEvent.loaded * 90) / progressEvent.total + 10);
                        setUploadProgress(percentCompleted);
                    }
                });
            } else if (uploadMethod === 'url') {
                setUploadStatus('Đang tải xuống từ URL...');
                const urlResponse = await axiosDirectApi.post(`/cv/datasets/${datasetId}/download-url`, {
                    url: uploadUrl,
                    auto_extract: true
                });

                // Check if extraction task was triggered
                if (urlResponse.data.extraction && urlResponse.data.extraction.task_id) {
                    const taskId = urlResponse.data.extraction.task_id;
                    console.log('[Upload] URL extraction task started:', taskId);

                    setUploadStatus('Đang xử lý dataset...');
                    setUploadProgress(90);

                    // Poll task status until completion
                    pollIntervalRef.current = setInterval(async () => {
                        try {
                            const statusResponse = await axiosDirectApi.get(`/cv/tasks/${taskId}`);
                            const taskStatus = statusResponse.data;

                            console.log('[Upload] Task status:', taskStatus);

                            if (taskStatus.status === 'PROGRESS') {
                                const taskProgress = taskStatus.progress || 0;
                                setUploadProgress(90 + taskProgress / 10);
                                setUploadStatus(taskStatus.message || 'Đang xử lý...');
                            } else if (taskStatus.status === 'SUCCESS') {
                                if (pollIntervalRef.current) {
                                    clearInterval(pollIntervalRef.current);
                                    pollIntervalRef.current = null;
                                }

                                setUploadProgress(100);
                                setUploadStatus('Hoàn thành!');
                                setUploading(false);

                                setTimeout(() => {
                                    onSuccess(datasetId);
                                    handleClose();
                                }, 1000);
                            } else if (taskStatus.status === 'FAILURE') {
                                if (pollIntervalRef.current) {
                                    clearInterval(pollIntervalRef.current);
                                    pollIntervalRef.current = null;
                                }
                                setError(taskStatus.error || 'Lỗi khi xử lý dataset');
                                setUploading(false);
                            }
                        } catch (pollErr) {
                            console.error('[Upload] Failed to poll task status:', pollErr);
                        }
                    }, 2000);
                } else {
                    // No extraction task (shouldn't happen with auto_extract=true)
                    setUploadProgress(100);
                    setUploadStatus('Hoàn thành!');
                    onSuccess(datasetId);
                    handleClose();
                }
            }

            // For folder/files method, mark as complete
            if (uploadMethod === 'folder' || uploadMethod === 'files') {
                setUploadProgress(100);
                setUploadStatus('Hoàn thành!');

                // Success
                setTimeout(() => {
                    onSuccess(datasetId);
                    handleClose();
                }, 1000);
            }
        } catch (err) {
            console.error('Upload error:', err);
            setError(err.response?.data?.detail || 'Lỗi khi tải lên dataset');
            setUploading(false);
            setUploadProgress(0);
        }
    };

    const handleClose = () => {
        if (!uploading) {
            // Cleanup polling interval
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }

            setUploadMethod('zip');
            setSelectedFiles([]);
            setDatasetName('');
            setUploadUrl('');
            setUploading(false);
            setUploadProgress(0);
            setUploadStatus('');
            setError('');
            onClose();
        }
    };

    const renderUploadArea = () => {
        if (uploadMethod === 'url') {
            return (
                <Box sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        label="URL Dataset"
                        placeholder="https://drive.google.com/file/d/xxx hoặc link trực tiếp"
                        value={uploadUrl}
                        onChange={(e) => setUploadUrl(e.target.value)}
                        disabled={uploading}
                        helperText="Hỗ trợ Google Drive, Dropbox, và direct download links"
                    />
                </Box>
            );
        }

        return (
            <Box sx={{ mt: 2 }}>
                <Paper
                    {...getRootProps()}
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        border: '2px dashed',
                        borderColor: isDragActive ? 'primary.main' : 'grey.300',
                        bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover'
                        }
                    }}
                >
                    <input {...getInputProps()} />
                    <CloudUpload sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                        {isDragActive ? 'Thả file vào đây...' : 'Kéo thả hoặc click để chọn file'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {uploadMethod === 'zip' && 'Chỉ chấp nhận file .zip'}
                        {uploadMethod === 'folder' && 'Chọn toàn bộ folder'}
                        {uploadMethod === 'files' && 'Chọn nhiều files (images + labels)'}
                    </Typography>
                </Paper>

                {/* Alternative file selection buttons */}
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        startIcon={uploadMethod === 'zip' ? <FolderZip /> : <InsertDriveFile />}
                    >
                        {uploadMethod === 'zip' ? 'Chọn File ZIP' : 'Chọn Files'}
                    </Button>
                    {uploadMethod === 'folder' && (
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => folderInputRef.current?.click()}
                            disabled={uploading}
                            startIcon={<Folder />}
                        >
                            Chọn Folder
                        </Button>
                    )}
                </Stack>

                <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept={uploadMethod === 'zip' ? '.zip' : '*'}
                    multiple={uploadMethod !== 'zip'}
                    onChange={handleFileSelect}
                />
                <input ref={folderInputRef} type="file" hidden webkitdirectory="" directory="" multiple onChange={handleFileSelect} />

                {/* Selected files list */}
                {selectedFiles.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Files đã chọn ({selectedFiles.length})
                        </Typography>
                        <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                            {selectedFiles.slice(0, 10).map((file, index) => (
                                <ListItem
                                    key={index}
                                    secondaryAction={
                                        <IconButton edge="end" size="small" onClick={() => handleRemoveFile(index)} disabled={uploading}>
                                            <Close />
                                        </IconButton>
                                    }
                                >
                                    <ListItemIcon>
                                        <InsertDriveFile fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary={file.name} secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`} />
                                </ListItem>
                            ))}
                            {selectedFiles.length > 10 && (
                                <ListItem>
                                    <ListItemText secondary={`...và ${selectedFiles.length - 10} files khác`} />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Tạo Dataset Mới
                <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }} disabled={uploading}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* Dataset Info */}
                <TextField
                    fullWidth
                    label="Tên Dataset"
                    value={datasetName}
                    onChange={(e) => setDatasetName(e.target.value)}
                    disabled={uploading}
                    required
                    sx={{ mb: 2 }}
                />

                <TextField
                    fullWidth
                    select
                    label="Loại Dataset"
                    value={datasetType}
                    onChange={(e) => setDatasetType(e.target.value)}
                    disabled={uploading}
                    SelectProps={{ native: true }}
                    sx={{ mb: 3 }}
                >
                    <option value="detection">Object Detection</option>
                    <option value="classification">Classification</option>
                    <option value="segmentation">Segmentation</option>
                    <option value="keypoint">Keypoint Detection</option>
                </TextField>

                {/* Upload Method Selector */}
                <Typography variant="subtitle2" gutterBottom>
                    Phương thức tải lên
                </Typography>
                <ToggleButtonGroup
                    value={uploadMethod}
                    exclusive
                    onChange={handleMethodChange}
                    fullWidth
                    disabled={uploading}
                    sx={{ mb: 2 }}
                >
                    <ToggleButton value="zip">
                        <Stack alignItems="center" spacing={0.5}>
                            <FolderZip />
                            <Typography variant="caption">ZIP File</Typography>
                        </Stack>
                    </ToggleButton>
                    <ToggleButton value="folder">
                        <Stack alignItems="center" spacing={0.5}>
                            <Folder />
                            <Typography variant="caption">Folder</Typography>
                        </Stack>
                    </ToggleButton>
                    <ToggleButton value="files">
                        <Stack alignItems="center" spacing={0.5}>
                            <InsertDriveFile />
                            <Typography variant="caption">Files</Typography>
                        </Stack>
                    </ToggleButton>
                    <ToggleButton value="url">
                        <Stack alignItems="center" spacing={0.5}>
                            <CloudDownload />
                            <Typography variant="caption">URL</Typography>
                        </Stack>
                    </ToggleButton>
                </ToggleButtonGroup>

                {/* Method description */}
                <Alert severity="info" sx={{ mb: 2 }}>
                    {uploadMethod === 'zip' && 'Tải lên file ZIP chứa dataset (khuyến nghị cho dataset lớn >1GB)'}
                    {uploadMethod === 'folder' && 'Kéo thả hoặc chọn toàn bộ folder dataset'}
                    {uploadMethod === 'files' && 'Chọn nhiều files (images, labels) để tải lên'}
                    {uploadMethod === 'url' && 'Tải dataset từ URL (Google Drive, Dropbox, ...)'}
                </Alert>

                {/* Upload Area */}
                {renderUploadArea()}

                {/* Upload Progress */}
                {uploading && (
                    <Box sx={{ mt: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Typography variant="body2">{uploadStatus}</Typography>
                            {uploadProgress === 100 && <CheckCircle color="success" fontSize="small" />}
                        </Stack>
                        <LinearProgress variant="determinate" value={uploadProgress} />
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                            {uploadProgress}%
                        </Typography>
                    </Box>
                )}

                {/* Error */}
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={uploading}>
                    Hủy
                </Button>
                <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={
                        uploading ||
                        !datasetName ||
                        (uploadMethod !== 'url' && selectedFiles.length === 0) ||
                        (uploadMethod === 'url' && !uploadUrl)
                    }
                    startIcon={<CloudUpload />}
                >
                    {uploading ? 'Đang tải lên...' : 'Tải lên'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DatasetUploadDialog;
